"use client"

import { Head, Link } from "@inertiajs/react"
import { ArrowLeft, ChevronLeft, ChevronRight } from "@untitledui/icons"
import { Suspense, lazy, useMemo } from "react"

import {
  examplesRegistry,
  getVariantList,
} from "@/components/ui-kit/untitled/_examples/_registry"
import UuiKitDemosLayout from "@/layouts/uui-kit-demos-layout"

import { Badge } from "@uui/base/badges/badges"
import { Button } from "@uui/base/buttons/button"

interface ExampleProps {
  category: string
  group: string
  variant: string
}

// Excluded demos reference `.demo` helper files (radar-charts.demo,
// inline-cta.demo) that aren't vendored in this UUI snapshot, so Vite
// fails the whole build when it tries to resolve their imports.
const exampleModules = import.meta.glob<{
  default?: React.ComponentType
  [key: string]: React.ComponentType | undefined
}>([
  "../../components/ui-kit/untitled/_examples/**/examples/**/*.tsx",
  "!../../components/ui-kit/untitled/_examples/application/dashboards-01/17/**",
  "!../../components/ui-kit/untitled/_examples/application/dashboards-02/08/**",
  "!../../components/ui-kit/untitled/_examples/application/dashboards-02/17/**",
  "!../../components/ui-kit/untitled/_examples/application/settings-02/13/**",
  "!../../components/ui-kit/untitled/_examples/application/settings-02/18/**",
  "!../../components/ui-kit/untitled/_examples/application/informational-01/08/**",
  "!../../components/ui-kit/untitled/_examples/application/informational-01/09/**",
  "!../../components/ui-kit/untitled/_examples/application/informational-01/10/**",
  "!../../components/ui-kit/untitled/_examples/application/informational-02/08/**",
  "!../../components/ui-kit/untitled/_examples/application/informational-02/09/**",
  "!../../components/ui-kit/untitled/_examples/application/informational-02/10/**",
  "!../../components/ui-kit/untitled/_examples/application/settings-01/01/**",
  "!../../components/ui-kit/untitled/_examples/application/settings-01/02/**",
  "!../../components/ui-kit/untitled/_examples/application/settings-02/01/**",
  "!../../components/ui-kit/untitled/_examples/application/settings-02/02/**",
  "!../../components/ui-kit/untitled/_examples/application/settings-01/13/**",
  "!../../components/ui-kit/untitled/_examples/application/settings-01/20/**",
  "!../../components/ui-kit/untitled/_examples/application/settings-02/20/**",
])

// Note: email-templates are excluded because the vendored UUI code has broken
// imports (references non-existent paths). The _shared directory contains
// internal utilities not meant to be rendered as standalone examples.
const sharedExampleModules = import.meta.glob<{
  default?: React.ComponentType
  [key: string]: React.ComponentType | undefined
}>([
  "../../components/ui-kit/untitled/shared-page-examples/**/*.tsx",
  "!../../components/ui-kit/untitled/shared-page-examples/email-templates/**",
  "!../../components/ui-kit/untitled/shared-page-examples/_shared/**",
])

const allModules = { ...exampleModules, ...sharedExampleModules }

function getComponentName(group: string, variant: string): string {
  if (/[a-z]/i.exec(variant)) {
    return variant
      .split("-")
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join("")
  }

  const parts = group.replace(/-\d+$/, "").split("-")
  const pascalCase = parts
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join("")
  const singular = pascalCase.endsWith("Pages")
    ? pascalCase.slice(0, -1)
    : pascalCase.endsWith("Posts")
      ? pascalCase.slice(0, -1)
      : pascalCase
  return `${singular}${variant}`
}

function getSharedExamplePath(group: string, variant: string): string {
  const base = `../../components/ui-kit/untitled/shared-page-examples/${group}/${variant}`
  if (group === "email-templates") {
    return `${base}/emails/${variant}.tsx`
  }
  if (group === "verification-pages") {
    return `${base}/components/application/modals/${variant}-modal.tsx`
  }
  const folderMap: Record<string, string> = {
    "login-pages": "login",
    "signup-pages": "signup",
    "forgot-password-pages": "forgot-password",
    "email-verification": "email-verification",
  }
  const folder = folderMap[group] || group.replace("-pages", "")
  return `${base}/components/shared-assets/${folder}/${variant}.tsx`
}

function getExamplePath(
  category: string,
  group: string,
  variant: string,
): string {
  if (category === "shared") {
    return getSharedExamplePath(group, variant)
  }
  const variantNum = variant.padStart(2, "0")
  if (category === "application") {
    return `../../components/ui-kit/untitled/_examples/${category}/${group}/${variantNum}/examples/${category}/${group}/${group.replace(/-\d+$/, "")}-${variantNum}.tsx`
  } else {
    const singularGroup = group.endsWith("s") ? group.slice(0, -1) : group
    return `../../components/ui-kit/untitled/_examples/${category}/${group}/${variantNum}/examples/${category}/${group}/${singularGroup}-${variantNum}.tsx`
  }
}

function ExampleNotFound({
  category,
  group,
  variant,
  triedPath,
  availablePaths,
}: ExampleProps & { triedPath?: string; availablePaths?: string[] }) {
  return (
    <div className="bg-primary flex min-h-screen items-center justify-center">
      <div className="max-w-2xl text-center">
        <h1 className="text-display-sm text-primary font-semibold">
          Example Not Found
        </h1>
        <p className="text-tertiary mt-2">
          Could not load example: {category}/{group}/{variant}
        </p>
        {triedPath && (
          <p className="text-tertiary bg-secondary mt-4 rounded p-2 font-mono text-xs break-all">
            Tried: {triedPath}
          </p>
        )}
        {availablePaths && availablePaths.length > 0 && (
          <div className="mt-4 text-left">
            <p className="text-tertiary mb-2 text-xs">
              Available paths (first 5):
            </p>
            <ul className="bg-secondary space-y-1 rounded p-2 font-mono text-xs">
              {availablePaths.map((p, i) => (
                <li key={i} className="text-tertiary break-all">
                  {p}
                </li>
              ))}
            </ul>
          </div>
        )}
        <Link
          href="/uui-demos/examples"
          className="text-brand-secondary hover:text-brand-secondary_hover mt-4 inline-flex items-center gap-2"
        >
          <ArrowLeft size={16} />
          Back to Examples
        </Link>
      </div>
    </div>
  )
}

function ExampleLoading() {
  return (
    <div className="bg-primary flex min-h-screen items-center justify-center">
      <div className="text-center">
        <div className="border-brand-solid mx-auto h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
        <p className="text-tertiary mt-4">Loading example...</p>
      </div>
    </div>
  )
}

export default function ExampleViewer({
  category,
  group,
  variant,
}: ExampleProps) {
  const categoryData = examplesRegistry[category]
  const groupData = categoryData?.groups[group]
  const variantList = groupData ? getVariantList(groupData) : []
  const currentIndex = variantList.indexOf(variant)
  const prevVariant = currentIndex > 0 ? variantList[currentIndex - 1] : null
  const nextVariant =
    currentIndex < variantList.length - 1 ? variantList[currentIndex + 1] : null

  const importPath = getExamplePath(category, group, variant)
  const availableKeys = Object.keys(allModules)

  const ExampleComponent = useMemo(() => {
    if (!allModules[importPath]) {
      console.warn(`Example not found: ${importPath}`)
      console.warn("Available paths:", availableKeys.slice(0, 10))
      return null
    }
    return lazy(async () => {
      const module = (await allModules[importPath]()) as Record<
        string,
        React.ComponentType
      >
      const componentName = getComponentName(group, variant)
      if (module.default) return { default: module.default }
      if (module[componentName]) return { default: module[componentName] }
      const altName = componentName.replace(/\d+$/, variant.padStart(2, "0"))
      if (module[altName]) return { default: module[altName] }
      const firstExport = Object.values(module).find(
        (v) => typeof v === "function",
      )!
      if (firstExport) return { default: firstExport }
      throw new Error(`No component found in module: ${importPath}`)
    })
  }, [category, group, variant, importPath, availableKeys])

  if (!categoryData || !groupData) {
    return (
      <ExampleNotFound
        category={category}
        group={group}
        variant={variant}
        triedPath={importPath}
        availablePaths={availableKeys.slice(0, 5)}
      />
    )
  }

  return (
    <>
      <Head title={`${groupData.name} #${variant} - UUI Examples`} />

      <div className="border-secondary bg-primary/95 fixed top-0 right-0 left-0 z-50 flex items-center justify-between border-b px-4 py-2 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <Link
            href="/uui-demos/examples"
            className="text-tertiary hover:text-primary flex items-center gap-1 text-sm"
          >
            <ChevronLeft size={16} />
            Examples
          </Link>
          <div className="flex items-center gap-2">
            <Badge color="gray" size="sm">
              {categoryData.name}
            </Badge>
            <span className="text-primary text-sm font-medium">
              {groupData.name}
            </span>
            <Badge color="brand" size="sm">
              #{variant}
            </Badge>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {prevVariant && (
            <Link
              href={`/uui-demos/examples/${category}/${group}/${prevVariant}`}
            >
              <Button color="secondary" size="sm" iconLeading={ChevronLeft}>
                Prev
              </Button>
            </Link>
          )}
          {nextVariant && (
            <Link
              href={`/uui-demos/examples/${category}/${group}/${nextVariant}`}
            >
              <Button color="secondary" size="sm" iconTrailing={ChevronRight}>
                Next
              </Button>
            </Link>
          )}
        </div>
      </div>

      <div className="pt-12">
        {ExampleComponent ? (
          <Suspense fallback={<ExampleLoading />}>
            <ExampleComponent />
          </Suspense>
        ) : (
          <ExampleNotFound
            category={category}
            group={group}
            variant={variant}
            triedPath={importPath}
            availablePaths={availableKeys.slice(0, 5)}
          />
        )}
      </div>
    </>
  )
}

ExampleViewer.layout = [UuiKitDemosLayout]
